import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Radio, CalendarDays, Archive, Search, X, Filter, CheckCircle2, XCircle, Star, Plus, Sparkles, Shuffle, Trophy, History, Loader2, SlidersHorizontal, LayoutList } from "lucide-react";
import { toast } from "sonner";
import { NavBar } from "@/components/NavBar";
import { ResultCard } from "@/components/ResultCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const REFRESH_MS = 120000;
const FAV_KEY = "fav4d_numbers";
const SETTINGS_KEY = "hub_settings";

const PROVIDER_LABELS = { jaguar: "J2 (Jaguar)", magnum: "Magnum", damacai: "Damacai", toto: "Toto", gdlotto: "GD Lotto", perdana: "Perdana", ninelotto: "9 Lotto", lucky: "Lucky", superj: "J3 (Super J)" };
const PROVIDER_DOT = { jaguar: "bg-slate-900", magnum: "bg-yellow-400", damacai: "bg-blue-700", toto: "bg-red-600", gdlotto: "bg-emerald-700", perdana: "bg-purple-700", ninelotto: "bg-cyan-700", lucky: "bg-pink-600", superj: "bg-orange-600" };

const sortStr = (s) => (s || "").split("").sort().join("");
const todayMYT = () => {
  const now = new Date();
  const myt = new Date(now.getTime() + now.getTimezoneOffset() * 60000 + 8 * 3600000);
  return myt.toISOString().slice(0, 10);
};

const CardSkeleton = () => (
  <div className="animate-pulse overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="h-14 bg-slate-200" />
    <div className="space-y-3 p-4">
      <div className="h-20 rounded-xl bg-slate-100" />
      <div className="grid grid-cols-5 gap-2">{Array.from({ length: 10 }).map((_, i) => <div key={i} className="h-9 rounded-lg bg-slate-100" />)}</div>
    </div>
  </div>
);

const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" }); } catch { return "—"; } };

const findHits = (r, num, permutation) => {
  const eq = (v) => v && v !== "----" && (permutation ? sortStr(v) === sortStr(num) : v === num);
  const hits = [];
  if (eq(r.first)) hits.push("1st");
  if (eq(r.second)) hits.push("2nd");
  if (eq(r.third)) hits.push("3rd");
  if ((r.special || []).some(eq)) hits.push("Special");
  if ((r.consolation || []).some(eq)) hits.push("Consolation");
  return hits;
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [search, setSearch] = useState("");
  const [permutation, setPermutation] = useState(false);
  const [podium, setPodium] = useState(false);
  const [classic, setClassic] = useState(false);
  const [histLoading, setHistLoading] = useState(false);
  const [histData, setHistData] = useState(null);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [hidden, setHidden] = useState(new Set());
  const [favorites, setFavorites] = useState([]);
  const [favInput, setFavInput] = useState("");
  const today = todayMYT();
  const isLive = selectedDate === "" || selectedDate === today;

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(FAV_KEY) || "[]");
      if (Array.isArray(saved)) setFavorites(saved);
    } catch (e) {
      console.warn("Gagal baca kegemaran dari localStorage", e);
    }
  }, []);
  const persistFav = (arr) => {
    setFavorites(arr);
    try {
      localStorage.setItem(FAV_KEY, JSON.stringify(arr));
    } catch (e) {
      console.warn("Gagal simpan kegemaran ke localStorage", e);
    }
  };

  // Load saved panel/filter preferences
  useEffect(() => {
    try {
      const s = JSON.parse(localStorage.getItem(SETTINGS_KEY) || "{}");
      if (typeof s.optionsOpen === "boolean") setOptionsOpen(s.optionsOpen);
      if (typeof s.permutation === "boolean") setPermutation(s.permutation);
      if (typeof s.podium === "boolean") setPodium(s.podium);
      if (typeof s.classic === "boolean") setClassic(s.classic);
      if (Array.isArray(s.hidden)) setHidden(new Set(s.hidden));
    } catch (e) {
      console.warn("Gagal baca tetapan dari localStorage", e);
    }
  }, []);

  // Persist panel/filter preferences (skip first run so we don't overwrite loaded values)
  const settingsLoaded = useRef(false);
  useEffect(() => {
    if (!settingsLoaded.current) { settingsLoaded.current = true; return; }
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify({ optionsOpen, permutation, podium, classic, hidden: [...hidden] }));
    } catch (e) {
      console.warn("Gagal simpan tetapan ke localStorage", e);
    }
  }, [optionsOpen, permutation, podium, classic, hidden]);

  const load = useCallback(async (dateArg, force = false, showToast = false) => {
    if (force) setRefreshing(true);
    const live = dateArg === "" || dateArg === today;
    try {
      const params = [];
      if (!live) params.push(`date=${dateArg}`);
      if (force) params.push("force=true");
      const res = await axios.get(`${API}/results${params.length ? "?" + params.join("&") : ""}`);
      setData(res.data);
      if (showToast) toast.success(live ? "Result dikemaskini" : `Result arkib ${dateArg} dimuat`);
    } catch (e) {
      if (showToast) toast.error("Gagal muat result. Cuba lagi.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [today]);

  useEffect(() => { load(selectedDate); }, [selectedDate, load]);
  useEffect(() => {
    if (!isLive) return;
    const interval = setInterval(() => load(selectedDate), REFRESH_MS);
    return () => clearInterval(interval);
  }, [isLive, selectedDate, load]);
  useEffect(() => { setHistData(null); }, [search, permutation]);

  const allResults = data?.results || [];
  const providers = [...new Set(allResults.map((r) => r.provider))];
  const results = allResults.filter((r) => !hidden.has(r.provider));

  const searchActive = /^\d{4}$/.test(search);
  const highlight = searchActive ? search : "";
  const matches = searchActive ? results.flatMap((r) => {
    const hits = findHits(r, search, permutation);
    return hits.length ? [{ name: r.title, hits }] : [];
  }) : [];

  // favorites won today (exact match against currently shown results)
  const favWins = {};
  favorites.forEach((fv) => {
    const where = allResults.flatMap((r) => (findHits(r, fv, false).length ? [r.title] : []));
    favWins[fv] = where;
  });

  const toggleProvider = (p) => setHidden((prev) => {
    const next = new Set(prev);
    next.has(p) ? next.delete(p) : next.add(p);
    return next;
  });

  const addFav = () => {
    if (!/^\d{4}$/.test(favInput)) { toast.error("Masukkan nombor 4 digit."); return; }
    if (favorites.includes(favInput)) { toast("Nombor sudah disimpan."); setFavInput(""); return; }
    persistFav([...favorites, favInput]);
    toast.success(`${favInput} disimpan ke kegemaran`);
    setFavInput("");
  };
  const removeFav = (n) => persistFav(favorites.filter((x) => x !== n));

  const luckyNumber = () => {
    const n = String(Math.floor(Math.random() * 10000)).padStart(4, "0");
    setPermutation(false);
    setSearch(n);
    toast.success(`Nombor bertuah anda hari ini: ${n}`);
  };

  const searchHistory = async () => {
    if (!searchActive) return;
    setHistLoading(true);
    try {
      const res = await axios.get(`${API}/search?number=${search}&days=30&permutation=${permutation}`);
      setHistData(res.data);
    } catch (e) {
      toast.error("Gagal cari dalam history.");
    } finally {
      setHistLoading(false);
    }
  };

  const searchHistoryDeep = async () => {
    if (!searchActive) return;
    setHistLoading(true);
    try {
      const res = await axios.get(`${API}/search?number=${search}&podium=true&since=2020-01-01&permutation=${permutation}`);
      setHistData(res.data);
      if (!res.data.reached) toast(`Diimbas sehingga ${res.data.scannedFrom}. Tekan lagi untuk imbas lebih lama.`);
    } catch (e) {
      toast.error("Gagal cari dalam history.");
    } finally {
      setHistLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <NavBar />

      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-[1500px] flex-col gap-3 px-4 py-4 sm:px-6">
          <div className="flex flex-wrap items-center gap-2.5">
            <div className="flex flex-1 items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 sm:flex-none">
              <CalendarDays className="h-4 w-4 shrink-0 text-slate-400" />
              <input data-testid="date-picker" type="date" max={today} value={selectedDate || today}
                onChange={(e) => setSelectedDate(e.target.value)} className="w-full bg-transparent text-sm font-medium text-slate-700 outline-none sm:w-auto" />
            </div>
            {!isLive && (
              <button data-testid="live-reset-button" onClick={() => setSelectedDate("")}
                className="flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
                <Radio className="h-4 w-4" /> Terkini
              </button>
            )}
            {!isLive && (
              <div className="flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700" data-testid="archive-badge">
                <Archive className="h-3.5 w-3.5" /> Mod Arkib
              </div>
            )}

            <div className="flex flex-1 items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 min-w-[140px] sm:flex-none">
              <Search className="h-4 w-4 shrink-0 text-slate-400" />
              <input data-testid="number-search" inputMode="numeric" maxLength={4} placeholder="Cari nombor 4D"
                value={search} onChange={(e) => setSearch(e.target.value.replace(/\D/g, "").slice(0, 4))}
                className="w-full bg-transparent text-sm font-medium text-slate-700 outline-none placeholder:text-slate-400 sm:w-28" />
              {search && <button data-testid="clear-search" onClick={() => setSearch("")} className="text-slate-400 hover:text-slate-700"><X className="h-3.5 w-3.5" /></button>}
            </div>

            <button data-testid="options-toggle" onClick={() => setOptionsOpen((v) => !v)}
              className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                optionsOpen ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-100"
              }`}>
              <SlidersHorizontal className="h-4 w-4" /> Pilihan
            </button>
          </div>

          {optionsOpen && (
            <div className="flex flex-col gap-3" data-testid="options-panel">
              <div className="flex flex-wrap items-center gap-2">
                <button data-testid="permutation-toggle" onClick={() => setPermutation((v) => !v)}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    permutation ? "border-emerald-500 bg-emerald-100 text-emerald-700" : "border-slate-200 bg-white text-slate-500 hover:bg-slate-100"
                  }`}>
                  <Shuffle className="h-3.5 w-3.5" /> Paw
                </button>

                <button data-testid="podium-toggle" onClick={() => setPodium((v) => !v)}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    podium ? "border-amber-500 bg-amber-100 text-amber-700" : "border-slate-200 bg-white text-slate-500 hover:bg-slate-100"
                  }`}>
                  <Trophy className="h-3.5 w-3.5" /> Podium
                </button>

                <button data-testid="classic-toggle" onClick={() => setClassic((v) => !v)}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    classic ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-500 hover:bg-slate-100"
                  }`}>
                  <LayoutList className="h-3.5 w-3.5" /> Classic View
                </button>

                <button data-testid="lucky-number-button" onClick={luckyNumber}
                  className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-1.5 text-sm font-bold text-white shadow-sm transition-transform hover:scale-105">
                  <Sparkles className="h-4 w-4" /> Luck
                </button>
              </div>

              {providers.length > 0 && (
                <div className="flex flex-wrap items-center gap-2" data-testid="provider-filter">
                  <span className="flex items-center gap-1 text-xs font-semibold text-slate-500"><Filter className="h-3.5 w-3.5" /> Tapis:</span>
                  {providers.map((p) => {
                    const off = hidden.has(p);
                    return (
                      <button key={p} data-testid={`filter-${p}`} onClick={() => toggleProvider(p)}
                        className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${off ? "border-slate-200 bg-white text-slate-400" : "border-slate-300 bg-slate-100 text-slate-800"}`}>
                        <span className={`h-2.5 w-2.5 rounded-full ${off ? "bg-slate-300" : PROVIDER_DOT[p]}`} />
                        {PROVIDER_LABELS[p] || p}
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2" data-testid="favorites-bar">
                <span className="flex items-center gap-1 text-xs font-semibold text-slate-500"><Star className="h-3.5 w-3.5 text-amber-500" /> Kegemaran:</span>
                <div className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-1">
                  <input data-testid="fav-input" inputMode="numeric" maxLength={4} placeholder="0000"
                    value={favInput} onChange={(e) => setFavInput(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    onKeyDown={(e) => e.key === "Enter" && addFav()}
                    className="w-16 bg-transparent text-sm font-semibold tabular text-slate-700 outline-none placeholder:text-slate-300" />
                  <button data-testid="fav-add" onClick={addFav} className="flex items-center rounded-full bg-amber-500 p-1 text-white hover:bg-amber-600"><Plus className="h-3.5 w-3.5" /></button>
                </div>
                {favorites.length === 0 && <span className="text-xs text-slate-400">Simpan nombor untuk auto-semak setiap hari</span>}
                {favorites.map((fv) => {
                  const won = (favWins[fv] || []).length > 0;
                  return (
                    <span key={fv} data-testid={`fav-chip-${fv}`}
                      className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold tabular ${won ? "border-emerald-400 bg-emerald-100 text-emerald-700" : "border-amber-300 bg-amber-50 text-amber-700"}`}>
                      <button onClick={() => { setPermutation(false); setSearch(fv); }} className="tabular">{fv}</button>
                      {won && <CheckCircle2 className="h-3.5 w-3.5" />}
                      <button data-testid={`fav-remove-${fv}`} onClick={() => removeFav(fv)} className="text-slate-400 hover:text-red-500"><X className="h-3 w-3" /></button>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <main className="mx-auto max-w-[1500px] px-4 pt-6 sm:px-6">
        {searchActive && (
          <div data-testid="search-banner" className={`mb-5 rounded-2xl border p-4 ${matches.length ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"}`}>
            {matches.length ? (
              <div className="flex flex-col gap-2">
                <p className="flex items-center gap-2 font-display text-lg font-bold text-emerald-700">
                  <CheckCircle2 className="h-5 w-5" /> {search} {permutation ? "(permutation) " : ""}MENANG di {matches.length} tempat!
                </p>
                <div className="flex flex-wrap gap-2">
                  {matches.map((m, i) => <span key={i} className="rounded-full border border-emerald-300 bg-white px-3 py-1 text-xs font-semibold text-emerald-800">{m.name}: {m.hits.join(", ")}</span>)}
                </div>
              </div>
            ) : (
              <p className="flex items-center gap-2 font-medium text-slate-500"><XCircle className="h-5 w-5 text-slate-400" /> {search} {permutation ? "(permutation) " : ""}tiada padanan pada result yang dipapar.</p>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              <button data-testid="history-search-button" onClick={searchHistory} disabled={histLoading}
                className="flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-100 disabled:opacity-60">
                {histLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <History className="h-3.5 w-3.5" />}
                Semak menang dalam 30 hari terakhir
              </button>
              <button data-testid="history-deep-button" onClick={searchHistoryDeep} disabled={histLoading}
                className="flex items-center gap-1.5 rounded-full border border-amber-400 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-100 disabled:opacity-60">
                {histLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trophy className="h-3.5 w-3.5" />}
                Semak podium sejak 2020
              </button>
            </div>
          </div>
        )}

        {searchActive && histData && histData.number === search && (
          <div data-testid="history-panel" className="mb-5 rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-3 flex items-center gap-2">
              {histData.podium ? <Trophy className="h-5 w-5 text-amber-500" /> : <History className="h-5 w-5 text-slate-500" />}
              <h3 className="font-display text-base font-bold text-slate-900">
                {histData.podium ? "Podium" : "History"} untuk {histData.number}{histData.permutation ? " (permutation)" : ""}
              </h3>
            </div>
            {histData.podium && (
              <p className="mb-3 text-xs text-slate-500" data-testid="history-scan-note">
                Diimbas sehingga <span className="font-semibold text-slate-700">{histData.scannedFrom}</span>
                {histData.reached ? " (sehingga 2020)" : " — tekan \"Semak podium sejak 2020\" lagi untuk imbas lebih lama."}
              </p>
            )}
            {histData.count ? (
              <div className="space-y-2">
                {histData.matches.map((m, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                    <span className="tabular w-24 text-sm font-bold text-slate-700">{m.date}</span>
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                      <span className={`h-2.5 w-2.5 rounded-full ${PROVIDER_DOT[m.provider] || "bg-slate-400"}`} />
                      {m.providerName} <span className="font-normal text-slate-400">({m.title})</span>
                    </span>
                    <span className="ml-auto flex flex-wrap gap-1">
                      {m.prizes.map((p, j) => (
                        <span key={j} className="flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-700">
                          {m.numbers?.[j] && <span className="tabular font-extrabold tracking-wider text-emerald-900">{m.numbers[j]}</span>}
                          <span className="font-semibold text-emerald-600">{p}</span>
                        </span>
                      ))}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">
                Tiada kemenangan {histData.podium ? "podium" : "dalam 30 hari terakhir"} untuk {histData.number}
                {histData.podium ? ` (diimbas sehingga ${histData.scannedFrom})` : ""}.
              </p>
            )}
          </div>
        )}

        {data?.updatedAt && (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-4 flex items-center gap-1.5 text-xs text-slate-500" data-testid="last-updated">
            <span className={`inline-block h-2 w-2 rounded-full ${isLive ? "animate-pulse bg-emerald-500" : "bg-amber-500"}`} />
            {isLive ? `Dikemaskini ${fmtTime(data.updatedAt)}` : `Arkib untuk ${selectedDate}`}
          </motion.p>
        )}

        <div className={`grid auto-rows-fr gap-2 sm:gap-3 ${classic ? "grid-cols-3" : "grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3"}`}>
          {loading
            ? Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)
            : results.map((r, i) => <ResultCard key={`${r.provider}-${r.title}`} result={r} index={i} highlight={highlight} permutation={permutation} favorites={favorites} podium={podium && !classic} classic={classic} />)}
        </div>

        {!loading && results.length === 0 && <div className="py-20 text-center text-slate-500" data-testid="no-results">Tiada result untuk dipaparkan.</div>}

        <footer className="mt-10 border-t border-slate-200 pt-6 text-center text-xs text-slate-400" data-testid="source-footer">
          <p className="mb-1 font-medium text-slate-500">Sumber (data langsung): J2 (Jaguar 4D) · Magnum · Damacai · Toto · GD Lotto · Perdana · 9 Lotto · Lucky Hari-Hari · J3 (Super J)</p>
          <p>Dikumpulkan secara automatik. Result adalah rujukan sahaja.</p>
        </footer>
      </main>
    </div>
  );
}
